import { useState, useContext, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Alert from '@mui/material/Alert';
import AuthContext from '../../context/AuthContext';
import MenuContext from '../../context/MenuContext';
import LoadingButton from '@mui/lab/LoadingButton';
import packageJson from '../../../package.json'; // Importar versión de package.json
import PlatformBridge from '../../utils/PlatformBridge';
import { getDefaultHomePath } from '../../utils/HomeNavigation';
import './Login.scss';

const Login = () => {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const { login } = useContext(AuthContext);
  const { fetchMenu } = useContext(MenuContext);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [terminalId, setTerminalId] = useState(localStorage.getItem("terminalId") || "");

  const routerNavigate = useNavigate(); // Para navegar sin permisos

  // Obtener el machineId del dispositivo desde Electron
  useEffect(() => {
    const fetchTerminalId = async () => {
      try {
        const id = await PlatformBridge.getTerminalId();
        setTerminalId(id);
        console.log("✅ TerminalId resuelto:", id);
      } catch (e) {
        console.error("❌ Error al obtener terminal ID:", e);
        // En caso de error, usar un ID de respaldo
        const fallbackId = `FALLBACK-${Date.now()}`;
        setTerminalId(fallbackId);
        localStorage.setItem("terminalId", fallbackId);
        console.warn("⚠️ Usando terminalId de respaldo:", fallbackId);
      }
    };

    fetchTerminalId();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const data = await login(email, password);
      // El resultado trae accessToken/refreshToken — no se loguea completo
      // ni siquiera en dev; alcanza con saber si vino bien o no.
      if (process.env.NODE_ENV !== "production") {
        console.log("📨 [Login] Login result", { status_code: data.status_code });
      }
      if (data.status_code === 200) {
        const menuResult = await fetchMenu();
        if (menuResult !== false) {
          routerNavigate(getDefaultHomePath(Array.isArray(menuResult) ? menuResult : undefined));
        } else {
          setError('Error cargando el menú.');
        }
      } else {
        // Traducir mensajes específicos del backend
        const errorKey = data.error_message;
        if (errorKey === 'empresa_not_found' || errorKey === 'invalid_credentials' || errorKey === 'company_blocked') {
          setError(t(errorKey));
        } else {
          setError(data.error_message || t('error_during_login') || 'Error durante el inicio de sesión');
        }
      }
    } catch (err) {
      console.error("💥 [Login] Unexpected submit error", {
        message: err?.message,
        status: err?.response?.status,
        data: err?.response?.data,
      });
      // El error ya fue manejado por AuthContext, pero traducir si es necesario
      const errorKey = err.response?.data?.error_message || err.response?.data?.message;
      if (errorKey === 'empresa_not_found' || errorKey === 'invalid_credentials' || errorKey === 'company_blocked') {
        setError(t(errorKey));
      } else {
        const errorMessage = errorKey || err.message || t('error_during_login') || 'Error durante el inicio de sesión';
        setError(errorMessage);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-container">
      <div className="login-form">
        <h2>Portal Axium</h2>
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label htmlFor="email">Correo Electrónico</label>
            <input
              type="text"
              id="email"
              inputMode="email"
              autoComplete="username"
              autoCapitalize="none"
              spellCheck={false}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
          </div>
          <div className="form-group">
            <label htmlFor="password">Contraseña</label>
            <input
              type="password"
              id="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
          </div>
          {error && (
            <Alert severity="error" style={{ marginBottom: '16px' }}>
              {error}
            </Alert>
          )}
          <LoadingButton
            type="submit"
            variant="contained"
            loading={loading}
            fullWidth
          >
            Ingresar
          </LoadingButton>
        </form>
      </div>

      {/* Footer con versión y terminalId */}
      <div className="login-footer">
        <p style={{ marginTop: '8px' }}>
          Versión: <strong>{packageJson.version}</strong>
        </p>
        <p style={{ marginTop: '8px' }}>
          Terminal ID: <strong>{terminalId || 'Cargando...'}</strong>
        </p>
      </div>
    </div>
  );
};

export default Login;
